import { useCallback, useEffect, useRef } from "react";
import { useAdminApplication } from "../../app/providers";
import type { AdminSessionSnapshot } from "../../app/session-lifecycle";
import type { Schemas } from "../workspace/api.generated";
import {
  authenticateAdmin,
  beginAdminTotpEnrollment,
  changeAdminPassword,
  confirmAdminTotpEnrollment,
} from "./api";

export function useAdminLoginCommand(): (
  input: Schemas["AuthenticateAdminRequest"],
) => Promise<AdminSessionSnapshot> {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  const activeRequest = useRef<ReturnType<typeof sessionLifecycle.createRequestController> | null>(null);

  useEffect(() => () => {
    activeRequest.current?.controller.abort();
    activeRequest.current?.release();
    activeRequest.current = null;
  }, []);

  return useCallback(async (input) => {
    if (activeRequest.current) throw new Error("ADMIN_LOGIN_ALREADY_PENDING");
    const request = sessionLifecycle.createRequestController();
    activeRequest.current = request;
    try {
      const response = await authenticateAdmin(apiClient, input, request.controller.signal);
      request.release();
      activeRequest.current = null;
      return sessionLifecycle.start(response);
    } finally {
      request.release();
      if (activeRequest.current === request) activeRequest.current = null;
    }
  }, [apiClient, sessionLifecycle]);
}

export function useAdminPasswordChangeCommand(): (
  input: Schemas["ChangeAdminPasswordRequest"],
) => Promise<AdminSessionSnapshot> {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  const activeRequest = useRef<ReturnType<typeof sessionLifecycle.createRequestController> | null>(null);

  useEffect(() => () => {
    activeRequest.current?.controller.abort();
    activeRequest.current?.release();
    activeRequest.current = null;
  }, []);

  return useCallback(async (input) => {
    if (activeRequest.current) throw new Error("ADMIN_PASSWORD_CHANGE_ALREADY_PENDING");
    const request = sessionLifecycle.createRequestController();
    activeRequest.current = request;
    try {
      const response = await changeAdminPassword(apiClient, input, request.controller.signal);
      request.release();
      activeRequest.current = null;
      return sessionLifecycle.start(response);
    } finally {
      request.release();
      if (activeRequest.current === request) activeRequest.current = null;
    }
  }, [apiClient, sessionLifecycle]);
}

export function useAdminTotpCommands(): {
  readonly beginEnrollment: () => Promise<Schemas["AdminTotpEnrollmentResponse"]>;
  readonly confirmEnrollment: (
    input: Schemas["ConfirmAdminTotpRequest"],
  ) => Promise<Schemas["AdminTotpConfirmationResponse"]>;
} {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  const activeRequest = useRef<ReturnType<typeof sessionLifecycle.createRequestController> | null>(null);

  useEffect(() => () => {
    activeRequest.current?.controller.abort();
    activeRequest.current?.release();
    activeRequest.current = null;
  }, []);

  const run = useCallback(async <T,>(operation: (signal: AbortSignal) => Promise<T>): Promise<T> => {
    if (activeRequest.current) throw new Error("ADMIN_TOTP_COMMAND_ALREADY_PENDING");
    const request = sessionLifecycle.createRequestController();
    activeRequest.current = request;
    try {
      return await operation(request.controller.signal);
    } finally {
      request.release();
      if (activeRequest.current === request) activeRequest.current = null;
    }
  }, [sessionLifecycle]);

  const beginEnrollment = useCallback(
    () => run((signal) => beginAdminTotpEnrollment(apiClient, signal)),
    [apiClient, run],
  );
  const confirmEnrollment = useCallback(
    (input: Schemas["ConfirmAdminTotpRequest"]) => (
      run((signal) => confirmAdminTotpEnrollment(apiClient, input, signal))
    ),
    [apiClient, run],
  );

  return { beginEnrollment, confirmEnrollment };
}
